// src/hooks/useDevStream.js
import { useState, useEffect, useRef, useCallback } from "react";
import logger from "../utils/logger";

const DEV_STREAM_WS_URL =
  process.env.REACT_APP_DEV_STREAM_WS_URL ||
  "wss://dev-streams.romancontrols.com";
const PING_INTERVAL_MS = 30000; // 30 seconds
const DEFAULT_MAX_EVENTS = 100; // Default FIFO limit
const MIN_MAX_EVENTS = 1; // Minimum allowed
const MAX_MAX_EVENTS = 300; // Maximum allowed
const RECONNECT_DELAY_MS = 3000; // 3 seconds

// localStorage keys
const STORAGE_KEY_FILTERS = "devStream_filters";
const STORAGE_KEY_YP_ENABLED = "devStream_ypEnabled";
const STORAGE_KEY_MC_ENABLED = "devStream_mcEnabled";
const STORAGE_KEY_MAX_EVENTS = "devStream_maxEvents";
const STORAGE_KEY_SELECTED_ORG = "devStream_selectedOrgId";

/**
 * Custom hook for Dev Stream WebSocket connection
 *
 * Features:
 * - WebSocket connection management with auto-reconnect
 * - Authentication flow (sends userId after connection)
 * - Ping mechanism (every 30s to keep connection alive)
 * - Event filtering (AND logic with negative search support)
 * - localStorage persistence for filters and source toggles
 * - 100-event FIFO limit
 *
 * @param {string} userId - User ID for authentication (e.g., Auth0 sub)
 * @param {boolean} isVisible - Whether dev stream pane is visible
 * @param {string} initialOrgId - Initial organization ID fallback (optional)
 * @returns {object} Hook state and methods
 */
export const useDevStream = (
  userId,
  isVisible = false,
  initialOrgId = null,
) => {
  const [connected, setConnected] = useState(false);
  const [authenticated, setAuthenticated] = useState(false);
  const [events, setEvents] = useState([]);
  const [connectionError, setConnectionError] = useState(null);
  // Always initialise from the currently active org — never restore a stale stored value.
  // The stored key is still written when the user manually picks an org inside the DevStream,
  // but it is never read on init or on re-open so that the stream always reflects the main app.
  const [selectedOrgId, setSelectedOrgId] = useState(
    initialOrgId || localStorage.getItem("selectedOrgId") || "",
  );

  // Load initial state from localStorage
  const [filters, setFilters] = useState(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY_FILTERS);
      return stored
        ? JSON.parse(stored)
        : {
            deviceId: "",
            installationId: "",
            organizationId: "",
            actionId: "",
            eventType: "",
            customSearch: "",
          };
    } catch (e) {
      logger.error("[DevStream] Failed to load filters from localStorage:", e);
      return {
        deviceId: "",
        actionId: "",
        eventType: "",
        customSearch: "",
      };
    }
  });

  const [ypEnabled, setYpEnabled] = useState(() => {
    const stored = localStorage.getItem(STORAGE_KEY_YP_ENABLED);
    return stored !== null ? stored === "true" : true;
  });

  const [mcEnabled, setMcEnabled] = useState(() => {
    const stored = localStorage.getItem(STORAGE_KEY_MC_ENABLED);
    return stored !== null ? stored === "true" : true;
  });

  const [maxEvents, setMaxEvents] = useState(() => {
    const stored = localStorage.getItem(STORAGE_KEY_MAX_EVENTS);
    if (stored) {
      const parsed = parseInt(stored, 10);
      if (
        !isNaN(parsed) &&
        parsed >= MIN_MAX_EVENTS &&
        parsed <= MAX_MAX_EVENTS
      ) {
        return parsed;
      }
    }
    return DEFAULT_MAX_EVENTS;
  });

  const wsRef = useRef(null);
  const pingIntervalRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);
  const shouldReconnectRef = useRef(false);
  const maxEventsRef = useRef(maxEvents);

  // Persist filters to localStorage
  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY_FILTERS, JSON.stringify(filters));
    } catch (e) {
      logger.error("[DevStream] Failed to save filters to localStorage:", e);
    }
  }, [filters]);

  // Persist source toggles to localStorage
  useEffect(() => {
    localStorage.setItem(STORAGE_KEY_YP_ENABLED, ypEnabled.toString());
  }, [ypEnabled]);

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY_MC_ENABLED, mcEnabled.toString());
  }, [mcEnabled]);

  useEffect(() => {
    maxEventsRef.current = maxEvents;
    localStorage.setItem(STORAGE_KEY_MAX_EVENTS, maxEvents.toString());

    // Trim existing events if new limit is lower
    setEvents((prev) => {
      if (prev.length > maxEvents) {
        return prev.slice(0, maxEvents);
      }
      return prev;
    });
  }, [maxEvents]);

  /**
   * Filter events based on current filter criteria
   * Uses AND logic (all non-empty filters must match)
   * Supports negative search with '-' prefix (e.g., '-unlock' excludes events containing 'unlock')
   *
   * Special organization filtering:
   * - Regular orgId: Only show events for that specific organization (exclude orphaned events)
   * - "ALL_ORGS": Show all events including orphaned events
   * - "ORPHANED": Show only events without organizationId (orphaned/system errors)
   */
  const matchesFilters = useCallback(
    (event) => {
      const data = event.data || {};
      const selectedOrgId = localStorage.getItem(STORAGE_KEY_SELECTED_ORG);

      // Extract organizationId from event metadata
      const eventOrgId =
        data._devStreamMetadata?.organizationId || data.organizationId;

      // Organization-based filtering
      if (selectedOrgId === "ORPHANED") {
        // ORPHANED mode: Only show events WITHOUT an organizationId
        if (eventOrgId) {
          return false; // Exclude events with an organizationId
        }
        // Continue to source and other filters for orphaned events
      } else if (selectedOrgId === "ALL_ORGS") {
        // ALL_ORGS mode: Show everything (no org filtering)
        // Continue to source and other filters
      } else if (selectedOrgId && selectedOrgId !== "ALL_ORGS") {
        // Regular organization mode: Only show events for this specific org
        // Exclude orphaned events (those without organizationId)
        if (eventOrgId !== selectedOrgId) {
          return false; // Exclude events from other orgs AND orphaned events
        }
      }

      // Source toggle filtering
      if (data.source === "YP" && !ypEnabled) return false;
      if (data.source === "MC" && !mcEnabled) return false;
      if (data.source === "SYSTEM_ERROR") return true; // Always show system errors

      // Helper to safely parse Message field if it's a JSON string
      const getParsedMessage = () => {
        if (data?.Message && typeof data.Message === "string") {
          try {
            return JSON.parse(data.Message);
          } catch (e) {
            return null;
          }
        }
        return null;
      };

      const parsedMessage = getParsedMessage();

      // Individual field filters
      const filterChecks = [
        {
          filterValue: filters.deviceId,
          dataValue:
            parsedMessage?.deviceId ||
            parsedMessage?.notifications?.[0]?.deviceId ||
            data.deviceId ||
            data.innerMessage?.deviceId,
        },
        {
          filterValue: filters.actionId,
          dataValue:
            parsedMessage?.actionId ||
            parsedMessage?.notifications?.[0]?.actionId ||
            data.innerMessage?.actionId,
        },
        {
          filterValue: filters.eventType,
          dataValue:
            data?.MessageAttributes?.type?.Value ||
            parsedMessage?.eventType ||
            data.eventType ||
            data?.Type, // SNS Type field (e.g., SubscriptionConfirmation)
        },
      ];

      // Check each non-empty filter
      for (const { filterValue, dataValue } of filterChecks) {
        if (!filterValue) continue; // Skip empty filters

        const filterStr = filterValue.toLowerCase();
        const dataStr = String(dataValue || "").toLowerCase();

        // Negative search: filter starts with '-'
        if (filterStr.startsWith("-")) {
          const negativeSearchTerm = filterStr.slice(1); // Remove '-' prefix
          if (dataStr.includes(negativeSearchTerm)) {
            return false; // Exclude if match found
          }
        } else {
          // Positive search: must contain filter value
          if (!dataStr.includes(filterStr)) {
            return false;
          }
        }
      }

      // Custom search (searches entire JSON payload)
      if (filters.customSearch) {
        const customSearchStr = filters.customSearch.toLowerCase();
        const eventJson = JSON.stringify(event).toLowerCase();

        if (customSearchStr.startsWith("-")) {
          const negativeSearchTerm = customSearchStr.slice(1);
          if (eventJson.includes(negativeSearchTerm)) {
            return false;
          }
        } else {
          if (!eventJson.includes(customSearchStr)) {
            return false;
          }
        }
      }

      return true; // All filters matched
    },
    [filters, ypEnabled, mcEnabled],
  );

  /**
   * Add event to the list (FIFO with configurable limit)
   */
  const addEvent = useCallback((eventData) => {
    setEvents((prev) => {
      const newEvent = {
        id: `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        timestamp: new Date().toISOString(),
        data: eventData,
      };

      // FIFO: Add to front, limit to maxEvents (use ref for latest value)
      // Note: Filtering happens at display time, not when adding
      const updated = [newEvent, ...prev];
      const currentMaxEvents = maxEventsRef.current;
      if (updated.length > currentMaxEvents) {
        return updated.slice(0, currentMaxEvents);
      }
      return updated;
    });
  }, []);

  /**
   * Start ping interval to keep connection alive
   */
  const startPingInterval = useCallback(() => {
    if (pingIntervalRef.current) {
      clearInterval(pingIntervalRef.current);
    }

    pingIntervalRef.current = setInterval(() => {
      if (wsRef.current?.readyState === WebSocket.OPEN) {
        logger.log("[DevStream] Sending ping");
        wsRef.current.send(JSON.stringify({ type: "ping" }));
      }
    }, PING_INTERVAL_MS);
  }, []);

  /**
   * Stop ping interval
   */
  const stopPingInterval = useCallback(() => {
    if (pingIntervalRef.current) {
      clearInterval(pingIntervalRef.current);
      pingIntervalRef.current = null;
    }
  }, []);

  /**
   * Authenticate with backend after connection
   */
  const authenticate = useCallback(() => {
    if (wsRef.current?.readyState === WebSocket.OPEN && userId) {
      let selectedOrgId = localStorage.getItem(STORAGE_KEY_SELECTED_ORG);

      // Check if user is trying to use special org modes (ALL_ORGS, ORPHANED)
      // These are only available to Full Admin users
      const isSpecialOrgMode =
        selectedOrgId === "ALL_ORGS" || selectedOrgId === "ORPHANED";

      if (isSpecialOrgMode) {
        // Clear special org mode and use regular org selection instead
        logger.warn(
          "[DevStream] Special org mode detected in localStorage, clearing for non-Full Admin authentication",
        );
        selectedOrgId = localStorage.getItem("selectedOrgId") || ""; // Fall back to main app's org selection
        localStorage.setItem(STORAGE_KEY_SELECTED_ORG, selectedOrgId);
      }

      logger.log("[DevStream] Sending authentication message", {
        userId,
        orgId: selectedOrgId,
      });
      wsRef.current.send(
        JSON.stringify({
          type: "authenticate",
          userId: userId,
          orgId: selectedOrgId, // Include current organization
          token: `Bearer ${userId}`, // Backend will validate userId against Users table
        }),
      );
    }
  }, [userId]);

  /**
   * Connect to WebSocket
   */
  const connect = useCallback(() => {
    if (
      wsRef.current?.readyState === WebSocket.OPEN ||
      wsRef.current?.readyState === WebSocket.CONNECTING
    ) {
      logger.log("[DevStream] Already connected or connecting");
      return;
    }

    logger.log("[DevStream] Connecting to:", DEV_STREAM_WS_URL);
    setConnectionError(null);

    try {
      const ws = new WebSocket(DEV_STREAM_WS_URL);
      wsRef.current = ws;

      ws.onopen = () => {
        logger.log("[DevStream] WebSocket connected");
        setConnected(true);
        setConnectionError(null);

        // Send authentication immediately after connection
        authenticate();

        // Start ping interval
        startPingInterval();
      };

      ws.onmessage = (event) => {
        try {
          const message = JSON.parse(event.data);
          logger.log(
            "[DevStream] Received message type:",
            message.type,
            "| Full message:",
            message,
          );

          switch (message.type) {
            case "authSuccess":
              logger.log("[DevStream] Authentication successful");
              setAuthenticated(true);
              break;

            case "authError":
              logger.error(
                "[DevStream] Authentication failed:",
                message.message || message.error,
              );
              setConnectionError(
                "Authentication failed: " +
                  (message.message || message.error || "Unknown error"),
              );
              setAuthenticated(false);
              break;

            case "event":
              // Add event to list
              addEvent(message.data);
              break;

            case "pong":
              logger.log("[DevStream] Received pong");
              break;

            case "testEventSent":
              logger.log("[DevStream] Test event sent confirmation");
              break;

            case "orgSwitchSuccess":
              logger.log(
                "[DevStream] Organization switch successful:",
                message.orgId,
              );
              break;

            default:
              logger.warn("[DevStream] Unknown message type:", message.type);
          }
        } catch (error) {
          logger.error("[DevStream] Error parsing message:", error);
        }
      };

      ws.onerror = (error) => {
        logger.error("[DevStream] WebSocket error:", error);
        setConnectionError("Connection error");
      };

      ws.onclose = (event) => {
        logger.log("[DevStream] WebSocket closed:", event.code, event.reason);
        setConnected(false);
        setAuthenticated(false);
        stopPingInterval();

        // Auto-reconnect if pane is still visible and we should reconnect
        if (shouldReconnectRef.current && isVisible) {
          logger.log(
            "[DevStream] Scheduling reconnect in",
            RECONNECT_DELAY_MS,
            "ms",
          );
          reconnectTimeoutRef.current = setTimeout(() => {
            connect();
          }, RECONNECT_DELAY_MS);
        }
      };
    } catch (error) {
      logger.error("[DevStream] Failed to create WebSocket:", error);
      setConnectionError("Failed to connect");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    userId,
    isVisible,
    authenticate,
    startPingInterval,
    stopPingInterval,
    addEvent,
  ]);

  /**
   * Disconnect from WebSocket
   */
  const disconnect = useCallback(() => {
    logger.log("[DevStream] Disconnecting");
    shouldReconnectRef.current = false;

    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }

    stopPingInterval();

    if (wsRef.current) {
      wsRef.current.close();
      wsRef.current = null;
    }

    setConnected(false);
    setAuthenticated(false);
  }, [stopPingInterval]);

  /**
   * Send test event
   */
  const sendTestEvent = useCallback(
    (count = 1, customPayload = null) => {
      if (wsRef.current?.readyState === WebSocket.OPEN && authenticated) {
        logger.log("[DevStream] Sending test event request:", {
          count,
          customPayload,
        });
        wsRef.current.send(
          JSON.stringify({
            type: "testEvent",
            count: Math.min(Math.max(count, 1), 10), // Clamp to 1-10
            customPayload: customPayload,
          }),
        );
      } else {
        logger.warn(
          "[DevStream] Cannot send test event - not connected or authenticated",
        );
      }
    },
    [authenticated],
  );

  /**
   * Clear all events
   */
  const clearEvents = useCallback(() => {
    setEvents([]);
  }, []);

  /**
   * Update filter
   */
  const updateFilter = useCallback((filterName, value) => {
    setFilters((prev) => ({
      ...prev,
      [filterName]: value,
    }));
  }, []);

  /**
   * Toggle YP events
   */
  const toggleYP = useCallback(() => {
    setYpEnabled((prev) => !prev);
  }, []);

  /**
   * Toggle MC events
   */
  const toggleMC = useCallback(() => {
    setMcEnabled((prev) => !prev);
  }, []);

  /**
   * Update maximum events limit with validation
   */
  const updateMaxEvents = useCallback((value) => {
    const numValue = parseInt(value, 10);
    if (isNaN(numValue)) {
      return;
    }

    // Clamp between MIN and MAX
    let clampedValue = numValue;
    if (clampedValue < MIN_MAX_EVENTS) {
      clampedValue = MIN_MAX_EVENTS;
    } else if (clampedValue > MAX_MAX_EVENTS) {
      clampedValue = MAX_MAX_EVENTS;
    }

    setMaxEvents(clampedValue);

    // Trim existing events if new limit is lower
    setEvents((prev) => {
      if (prev.length > clampedValue) {
        return prev.slice(0, clampedValue);
      }
      return prev;
    });
  }, []);

  /**
   * Switch organization without reconnecting WebSocket
   */
  const switchOrganization = useCallback(
    (newOrgId) => {
      if (wsRef.current?.readyState === WebSocket.OPEN && authenticated) {
        logger.log("[DevStream] Switching organization to:", newOrgId);

        // Send organization switch message to backend
        wsRef.current.send(
          JSON.stringify({
            type: "updateOrganization",
            orgId: newOrgId,
          }),
        );

        // Note: Events are NOT cleared - view filter will handle display based on organizationId
        // This allows users to see event history across different organization views

        // Update localStorage and state (using DevStream-specific key to avoid affecting main app)
        localStorage.setItem(STORAGE_KEY_SELECTED_ORG, newOrgId);
        setSelectedOrgId(newOrgId);
      } else {
        logger.warn(
          "[DevStream] Cannot switch organization - not connected or authenticated",
        );
      }
    },
    [authenticated],
  );

  /**
   * Get filtered events based on current source toggles and filter criteria
   */
  const filteredEvents = useCallback(() => {
    return events.filter((event) => matchesFilters(event));
  }, [events, matchesFilters]);

  // Connect/disconnect based on visibility.
  // On every open, reset to the currently active org so stale settings are never restored.
  useEffect(() => {
    if (isVisible && userId) {
      const currentOrg =
        initialOrgId || localStorage.getItem("selectedOrgId") || "";
      setSelectedOrgId(currentOrg);
      localStorage.setItem(STORAGE_KEY_SELECTED_ORG, currentOrg);
      setEvents([]); // Clear any events from the previous session
      shouldReconnectRef.current = true;
      connect();
    } else {
      disconnect();
    }

    return () => {
      disconnect();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isVisible, userId]); // Only reconnect on visibility or userId change

  // When the main app's active organisation changes, immediately sync the DevStream.
  // This handles issue #2: switching org/installation while the pane is open.
  useEffect(() => {
    if (!initialOrgId) return;
    if (initialOrgId === selectedOrgId) return;

    logger.log(
      "[DevStream] Active org changed externally, resetting to:",
      initialOrgId,
    );

    setSelectedOrgId(initialOrgId);
    localStorage.setItem(STORAGE_KEY_SELECTED_ORG, initialOrgId);
    setEvents([]); // Clear stale events from the old org

    // If already connected, send an org-switch message on the live socket
    if (authenticated && wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(
        JSON.stringify({ type: "updateOrganization", orgId: initialOrgId }),
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialOrgId]);

  return {
    // Connection state
    connected,
    authenticated,
    connectionError,

    // Events
    events: filteredEvents(),
    clearEvents,

    // Filters
    filters,
    updateFilter,

    // Source toggles
    ypEnabled,
    mcEnabled,
    toggleYP,
    toggleMC,

    // Test events
    sendTestEvent,

    // Organization switching
    switchOrganization,
    selectedOrgId,

    // Max events configuration
    maxEvents,
    updateMaxEvents,

    // Manual connection control (if needed)
    connect,
    disconnect,
  };
};

export default useDevStream;
